import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { TransitionProps } from "@mui/material/transitions";
import QRCode from "qrcode.react";
import React, { useState } from "react";
import jsPDF from "jspdf";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface FragranceData {
  fragranceId: number;
  slot: number;
  name: string;
  selected: boolean;
  customerCode: string;
  dueDate: string;
  serialNumber: string;
}

interface QRCodesGridProps {
  name: string;
  data: Array<FragranceData>;
  open: boolean;
  onClose: () => void;
}

const formatDataToString = (data: FragranceData): string => {
  const dateObject = new Date(data.dueDate);
  const day = ("0" + dateObject.getUTCDate()).slice(-2);
  const month = ("0" + (dateObject.getUTCMonth() + 1)).slice(-2);
  const year = dateObject
    .getUTCFullYear()
    .toString()
    .slice(-2);

  const formattedId = data.fragranceId.toString().padStart(4, "0");

  return `${data.customerCode}${formattedId}${data.serialNumber}${day}${month}${year}`;
};

const downloadQRCode = (data: FragranceData, index: number) => {
  const canvas = document.getElementById(`qr-code-${index}`) as HTMLCanvasElement;
  if (canvas) {
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `QRCode-Slot-${data.slot}-${data.name}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }
};

const downloadSingleQRCodeLarge = (data: FragranceData) => {
  const canvas = document.getElementById(
    `qr-code-large-${data.fragranceId}`
  ) as HTMLCanvasElement;
  if (canvas) {
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `QRCode-Slot-${data.slot}-${data.name}-Large.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }
};

const exportAllToPDF = async (data: Array<FragranceData>, campaignName: string) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const qrSize = 50;
  const cols = 3;
  const rows = 4;
  const spacingX = (pageWidth - 2 * margin) / cols;
  const spacingY = (pageHeight - 2 * margin - 20) / rows;

  let itemsOnPage = 0;

  for (let i = 0; i < data.length; i++) {
    if (itemsOnPage === 0 && i > 0) {
      pdf.addPage();
    }

    if (itemsOnPage === 0) {
      pdf.setFontSize(16);
      pdf.text(`QR Codes - ${campaignName}`, pageWidth / 2, 15, {
        align: "center",
      });
    }

    const col = itemsOnPage % cols;
    const row = Math.floor(itemsOnPage / cols);

    const x = margin + col * spacingX + spacingX / 2 - qrSize / 2;
    const y = margin + 20 + row * spacingY;

    const canvas = document.getElementById(`qr-code-${i}`) as HTMLCanvasElement;
    if (canvas) {
      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", x, y, qrSize, qrSize);

      pdf.setFontSize(8);
      const text = `Slot ${data[i].slot} - ${data[i].name}`;
      pdf.text(text, x + qrSize / 2, y + qrSize + 5, { align: "center" });

      pdf.setFontSize(7);
      pdf.text(formatDataToString(data[i]), x + qrSize / 2, y + qrSize + 10, {
        align: "center",
      });
    }

    itemsOnPage++;
    if (itemsOnPage >= cols * rows) {
      itemsOnPage = 0;
    }
  }

  pdf.save(`QRCodes-${campaignName}.pdf`);
};

const QRCodesGrid: React.FC<QRCodesGridProps> = ({
  name,
  data,
  open,
  onClose,
}) => {
  const [selectedQRCodeIndex, setSelectedQRCodeIndex] = useState<number | null>(
    null,
  );

  const handleQRCodeClick = (index: number) => {
    setSelectedQRCodeIndex(index);
  };

  const handleCloseQRCodeDialog = () => {
    setSelectedQRCodeIndex(null);
  };

  const handlePrev = () => {
    if (selectedQRCodeIndex !== null && selectedQRCodeIndex > 0) {
      setSelectedQRCodeIndex(selectedQRCodeIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedQRCodeIndex !== null && selectedQRCodeIndex < data.length - 1) {
      setSelectedQRCodeIndex(selectedQRCodeIndex + 1);
    }
  };

  const handleExportAllToPDF = () => {
    exportAllToPDF(data, name);
  };

  return (
    <>
      <Dialog
        TransitionComponent={Transition}
        keepMounted
        maxWidth="md"
        fullWidth
        open={open}
        onClose={onClose}
      >
        <DialogTitle textAlign={"center"} sx={{ cursor: "none" }}>
          QR Codes para configuração: {name}
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={2}
            columns={{ xs: 4, sm: 8, md: 12 }}
            sx={{ flexGrow: 1, margin: 2 }}
          >
            {data.map((item, index) => (
              <Grid key={index} xs={4} sm={4} md={3}>
                <Card key={index}>
                  <Stack padding={0.5}>
                    <Tooltip title="ABRIR" arrow>
                      <IconButton onClick={() => handleQRCodeClick(index)}>
                        <QRCode
                          id={`qr-code-${index}`}
                          value={formatDataToString(item)}
                        />
                      </IconButton>
                    </Tooltip>
                    <Typography align="center" sx={{ cursor: "none" }}>
                      {`Slot: ${item.slot} - ${item.name}`}
                    </Typography>
                    <Typography align="center" sx={{ cursor: "none" }}>
                      {formatDataToString(item)}
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => handleQRCodeClick(index)}
                      >
                        Abrir
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => downloadQRCode(item, index)}
                      >
                        Baixar
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportAllToPDF}
          >
            Exportar Todos (PDF)
          </Button>
          <Button
            sx={{ flex: 1 }}
            variant="contained"
            color="error"
            onClick={onClose}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        TransitionComponent={Transition}
        open={selectedQRCodeIndex !== null}
        onClose={handleCloseQRCodeDialog}
        maxWidth="sm"
        fullWidth
        style={{ backdropFilter: "blur(20px)" }}
      >
        <DialogTitle sx={{ cursor: "none" }}>
          QR Code:{" "}
          {selectedQRCodeIndex !== null &&
            formatDataToString(data[selectedQRCodeIndex])}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <IconButton
            onClick={handlePrev}
            title="Anterior"
            disabled={selectedQRCodeIndex === 0}
          >
            <ArrowBackIos />
          </IconButton>
          {selectedQRCodeIndex !== null && (
            <Box>
              <QRCode
                id={`qr-code-large-${data[selectedQRCodeIndex].fragranceId}`}
                value={formatDataToString(data[selectedQRCodeIndex])}
                size={256}
              />
              <Typography align="center" sx={{ cursor: "none", marginTop: 2 }}>
                {`Slot: ${data[selectedQRCodeIndex].slot} - ${data[selectedQRCodeIndex].name}`}
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={handleNext}
            title="Próxima"
            disabled={selectedQRCodeIndex === data.length - 1}
          >
            <ArrowForwardIos />
          </IconButton>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              selectedQRCodeIndex !== null &&
              downloadSingleQRCodeLarge(data[selectedQRCodeIndex])
            }
          >
            Baixar Imagem
          </Button>
          <Button
            sx={{ flex: 1 }}
            variant="contained"
            color="error"
            onClick={handleCloseQRCodeDialog}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QRCodesGrid;
